import { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useFieldArray, useForm } from "react-hook-form";
import { useContextStateManagement } from "../context/ContextApi";
import { logout } from "../features/userSlice";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faCircleXmark,
} from "@fortawesome/free-solid-svg-icons";
import { debounce } from "lodash";
import { AttributeField } from "./AttributeField";
import { ColdTransferCsvUpload } from "./ColdTransferCsvUpload";
import { getFunctionSections, findFunctionSection } from "./functionSections";
import "./ApiIntegration.css";

const DEFAULT_API_TIMEOUT_SEC = 60;
const DEFAULT_API_RETRY_COUNT = 0;
const DEFAULT_API_PERIODIC_MESSAGE =
  "I’m still checking that for you, please hold for a moment.";

const toRequiredSelectValue = (value) => {
  if (value === true || String(value).trim().toLowerCase() === "yes" || String(value).trim().toLowerCase() === "true") {
    return "true";
  }
  if (value === false || String(value).trim().toLowerCase() === "no" || String(value).trim().toLowerCase() === "false") {
    return "false";
  }
  return "";
};

const fromRequiredSelectValue = (value) => value === true || String(value).trim().toLowerCase() === "true";
const toSourceSelectValue = (value) =>
  String(value || "model").trim().toLowerCase() === "constant"
    ? "constant"
    : "model";
const fromSourceSelectValue = (value) =>
  String(value || "model").trim().toLowerCase() === "constant"
    ? "constant"
    : "model";
const toAliasInputValue = (value) =>
  Array.isArray(value)
    ? value.join(", ")
    : String(value || "").trim();
const fromAliasInputValue = (value) =>
  String(value || "")
    .replace(/\n/g, ",")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const buildDefaultFormValues = (functionType = "apiFunction") => ({
  name: "",
  description: "",
  url: "",
  func_type: functionType,
  execution_mode: "standard",
  requestType: "",
  auth_header: "",
  timeout_sec: DEFAULT_API_TIMEOUT_SEC,
  retry_count: DEFAULT_API_RETRY_COUNT,
  periodic_message: DEFAULT_API_PERIODIC_MESSAGE,
  attributes: [],
  routing_rules: [],
  handover_receiver_phone_number: "",
  ext: "",
  transfer_line: "",
  ext_delay_sec: "",
  failure_mode: "default",
  failure_prompt: "",
  warm_summary_prompt: "",
});

const mapApiAttributes = (attributes = []) =>
  attributes.map((attr) => ({
    name: attr.Name,
    attributeId: attr.Id,
    description: attr.Description,
    attr_type: attr.AttributeType,
    required: toRequiredSelectValue(attr.Required),
    source: toSourceSelectValue(attr.Source),
    default_value: attr.DefaultValue ?? "",
    aliases: toAliasInputValue(attr.Aliases),
    childAttributes: (attr.Attributes || []).map((child) => ({
      name: child.Name,
      attributeId: child.Id,
      description: child.Description,
      attr_type: child.AttributeType,
      required: toRequiredSelectValue(child.Required),
      source: toSourceSelectValue(child.Source),
      default_value: child.DefaultValue ?? "",
      aliases: toAliasInputValue(child.Aliases),
    })),
  }));

const mapRoutingRules = (rules = []) =>
  rules.map((rule) => ({
    field: rule.Field || "",
    min: rule.Min ?? "",
    max: rule.Max ?? "",
    target_url: rule.TargetUrl || "",
    target_auth_header: rule.TargetAuthorizationHeader || "",
  }));

const buildApiFunctionValues = (functionData) => ({
  ...buildDefaultFormValues("apiFunction"),
  func_type: "apiFunction",
  name: functionData?.Name || "",
  description: functionData?.Description || "",
  execution_mode:
    String(functionData?.ExecutionMode || "standard").trim().toLowerCase() ===
    "stateful"
      ? "stateful"
      : "standard",
  requestType:
    functionData?.RequestType === "UPDATE"
      ? "PATCH"
      : functionData?.RequestType || "",
  url: functionData?.URL || "",
  auth_header: functionData?.AuthorizationHeader || "",
  timeout_sec: functionData?.TimeoutSec ?? DEFAULT_API_TIMEOUT_SEC,
  retry_count: functionData?.RetryCount ?? DEFAULT_API_RETRY_COUNT,
  periodic_message:
    functionData?.PeriodicMessage || DEFAULT_API_PERIODIC_MESSAGE,
  attributes: mapApiAttributes(functionData?.Attributes || []),
  routing_rules: mapRoutingRules(functionData?.RoutingRules || []),
});

const buildTransferFunctionValues = (functionType, functionData) => ({
  ...buildDefaultFormValues(functionType),
  func_type: functionType,
  name: functionData?.Name || "",
  description: functionData?.Description || "",
  handover_receiver_phone_number:
    functionData?.HandoverReceiverPhoneNumber || "",
  ext: functionData?.Ext || "",
  ext_delay_sec:
    functionData?.ExtDelaySec === undefined || functionData?.ExtDelaySec === null
      ? ""
      : String(functionData.ExtDelaySec),
  transfer_line: functionData?.TransferLine || "",
  ...(functionType === "warm_transfer" && {
    warm_summary_prompt: functionData?.WarmSummaryPrompt || "",
    failure_mode: functionData?.FailureMode === "custom" ? "custom" : "default",
    failure_prompt: functionData?.FailurePrompt || "",
  }),
});

// Shared by both the create-mode single-submit flow (onSubmit) and the edit-mode per-section
// save flow (saveSection) below - pulled out so a save that only touches the Attributes section
// formats attributes exactly the same way a full submit always has.
const formatAttributesForSave = (data) => {
  const isStatefulExecution = data.execution_mode === "stateful";
  return (data.attributes || []).map((attr) => ({
    Name: attr.name,
    Description: attr.description,
    AttributeType: attr.attr_type,
    Required: fromRequiredSelectValue(attr.required),
    ...(isStatefulExecution && {
      Source: fromSourceSelectValue(attr.source),
      DefaultValue: attr.default_value ?? "",
      Aliases: fromAliasInputValue(attr.aliases),
    }),
    Attributes: (attr.childAttributes || []).map((child) => ({
      Name: child.name,
      Description: child.description,
      AttributeType: child.attr_type,
      Required: fromRequiredSelectValue(child.required),
      ...(isStatefulExecution && {
        Source: fromSourceSelectValue(child.source),
        DefaultValue: child.default_value ?? "",
        Aliases: fromAliasInputValue(child.aliases),
      }),
    })),
  }));
};

// RoutingRules: deterministic routing between multiple backing endpoints based on a computed
// day-delta for one of this tool's own date attributes (Field). Rows with no Field or no Target
// URL are dropped - the backend re-validates this too, this is just so an obviously-incomplete
// row doesn't get sent at all.
const formatRoutingRulesForSave = (data) =>
  (data.routing_rules || [])
    .filter((rule) => rule.field && rule.target_url)
    .map((rule) => ({
      Field: rule.field,
      TargetUrl: rule.target_url,
      ...(rule.min !== "" && rule.min !== undefined
        ? { Min: Number(rule.min) }
        : {}),
      ...(rule.max !== "" && rule.max !== undefined
        ? { Max: Number(rule.max) }
        : {}),
      ...(rule.target_auth_header
        ? { TargetAuthorizationHeader: rule.target_auth_header }
        : {}),
    }));

export const FunctionModal = ({
  show,
  handleClose,
  memberType,
  fetchApiHandle,
  editFunctionData,
  initialFunctionType = "apiFunction",
}) => {
  const {
    PracticeId,
    token,
    usersPostRequest,
    setToken,
    setUserType,
    dateRangeRequest,
    setActiveTab,
  } = useContextStateManagement();
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    getValues,
    trigger,
    formState: { errors, isValidating, isSubmitting },
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: buildDefaultFormValues(initialFunctionType),
  });
  const requestTypeValue = watch("requestType");
  const functionType = watch("func_type");
  const executionMode = watch("execution_mode");
  const [successMsg, setSuccessMsg] = useState("");
  const [, setDeletedAttributeIds] = useState([]);
  const [, setDeletedChildAttributeIds] = useState([]);
  const [availability, setAvailability] = useState(false);
  const [functionName, setFunctionName] = useState("");
  const originalEditData = useRef(editFunctionData || null);
  const [currentFunctionType, setCurrentFunctionType] = useState("");
  const [hasOutboundTrunk, setHasOutboundTrunk] = useState(false);
  // Per-section Save state (edit mode only) - keyed by sectionId, same shape as Prompt Hub's own
  // sectionSaveState/sectionSaveError (PAuthQOnBoardingForm.js).
  const [activeSection, setActiveSection] = useState(null);
  const [sectionSaveState, setSectionSaveState] = useState({});
  const [sectionSaveError, setSectionSaveError] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "attributes",
  });

  const {
    fields: routingRuleFields,
    append: appendRoutingRule,
    remove: removeRoutingRule,
  } = useFieldArray({
    control,
    name: "routing_rules",
  });
  const watchedAttributes = watch("attributes") || [];
  const routableAttributeNames = watchedAttributes
    .map((attr) => attr?.name)
    .filter(Boolean);

  // The left-nav + right-panel per-section layout only applies while editing an EXISTING
  // function of its ORIGINAL type (the common case this fix targets: a save of one section must
  // not blow away every other section). Creating a brand new function has no prior row to lose
  // data from, and switching a function's type mid-edit (existing, pre-this-change behavior -
  // the "Restore saved values" / "Switching type -> reset blank defaults" effect below) already
  // relies on a single flat-overwrite submit to make sense of the transition cleanly - both keep
  // today's single-scrolling-form + one Submit button exactly as before.
  const isEditingSameType =
    Boolean(editFunctionData) &&
    Boolean(currentFunctionType) &&
    currentFunctionType === (originalEditData.current?.FunctionType || "");
  const sections = useMemo(
    () => (isEditingSameType ? getFunctionSections(functionType) : []),
    [isEditingSameType, functionType]
  );

  useEffect(() => {
    if (sections.length === 0) {
      setActiveSection(null);
      return;
    }
    setActiveSection((prev) =>
      prev && sections.some((section) => section.id === prev) ? prev : sections[0].id
    );
  }, [sections]);

  const checkAvailability = useRef(
    debounce(async (name, resolve) => {
      try {
        let obj = { practiceId: PracticeId, functionName: name };
        const response = await dateRangeRequest(
          token,
          "/api/checkFunctionAvailability",
          obj
        );
        if (response === "Token is expired, login again") {
          setIsTokenExpired(true);
        } else if (response && response.availability) {
          setAvailability(true);
          resolve(true);
        } else {
          setAvailability(false);
          resolve("Function name is not available");
        }
      } catch (error) {
        setAvailability(false);
        resolve("Error checking name availability");
      }
    })
  ).current;

  const onSubmit = async (data) => {
    const formattedAttributes =
      functionType === "apiFunction" && formatAttributesForSave(data);
    const formattedRoutingRules =
      functionType === "apiFunction" && formatRoutingRulesForSave(data);

    let obj = {
      practiceId: PracticeId,
      memberType,
      functionData: {
        // ...data,
        Id: editFunctionData?.Id,
        Name: data.name,
        FunctionType: data.func_type,
        Description: data.description,
        ...(functionType === "apiFunction" && {
          ExecutionMode: data.execution_mode,
          AuthorizationHeader: data.auth_header,
          URL: data.url,
          RequestType: data.requestType,
          TimeoutSec: data.timeout_sec,
          RetryCount: data.retry_count,
          PeriodicMessage:
            data.periodic_message?.trim() || DEFAULT_API_PERIODIC_MESSAGE,
          Attributes: formattedAttributes,
          RoutingRules: formattedRoutingRules,
        }),

        ...((functionType === "warm_transfer" ||
          functionType === "cold_transfer") && {
          HandoverReceiverPhoneNumber: data.handover_receiver_phone_number,
          //HandoverInitiatorPhoneNumber: data.handover_initiator_phone_number,
          Ext: data.ext,
          ...(String(data.ext_delay_sec ?? "").trim() !== "" && {
            ExtDelaySec: String(data.ext_delay_sec).trim(),
          }),
          TransferLine: data.transfer_line,
        }),

        ...(functionType === "warm_transfer" && {
          WarmSummaryPrompt: data.warm_summary_prompt,
          ...(data.failure_mode === "custom" && {
            FailureMode: "custom",
            FailurePrompt: data.failure_prompt,
          }),
        }),
      },
    };

    let url = `/api/apiFunction`;

    const userData = await usersPostRequest(token, url, obj);
    if (userData === "Token is expired, login again") {
      setIsTokenExpired(true);
    } else if (
      userData === "Function added successfully" ||
      userData === "Function updated successfully"
    ) {
      setSuccessMsg(userData);
      fetchApiHandle("functionModal");
      reset(buildDefaultFormValues());
      setAvailability(false);
      setDeletedAttributeIds([]);
      setDeletedChildAttributeIds([]);
      setFunctionName("");
      setCurrentFunctionType("");
      // setTimeout(() => {
      //     setSuccessMsg("")
      // }, 4000);
    }
  };

  // Builds the request body for one section's own Save button. This used to send only the
  // fields that section owns, on the assumption that the backend's POST /api/apiFunction update
  // branch merges an omitted field against the row's existing "Functions" JSON - it doesn't: that
  // branch is a full overwrite, so any field left out of the request gets wiped from the row
  // (e.g. omitting AuthorizationHeader this way has crashed a later handoff with a KeyError).
  // getValues() already reflects this section's edits folded into the full current form state
  // (every field was populated via reset() when the modal opened in edit mode, and react-hook-form
  // keeps unmounted fields' values by default), so always send the function's complete, current
  // definition rather than just this section's fields.
  const buildSectionPayload = (data, sectionId) => {
    const section = findFunctionSection(functionType, sectionId);
    if (!section) return null;

    return {
      Id: editFunctionData?.Id,
      Name: data.name,
      FunctionType: data.func_type,
      Description: data.description,
      ...(functionType === "apiFunction" && {
        ExecutionMode: data.execution_mode,
        AuthorizationHeader: data.auth_header,
        URL: data.url,
        RequestType: data.requestType,
        TimeoutSec: data.timeout_sec,
        RetryCount: data.retry_count,
        PeriodicMessage:
          data.periodic_message?.trim() || DEFAULT_API_PERIODIC_MESSAGE,
        Attributes: formatAttributesForSave(data),
        RoutingRules: formatRoutingRulesForSave(data),
      }),
      ...((functionType === "warm_transfer" || functionType === "cold_transfer") && {
        HandoverReceiverPhoneNumber: data.handover_receiver_phone_number,
        Ext: data.ext,
        ...(String(data.ext_delay_sec ?? "").trim() !== "" && {
          ExtDelaySec: String(data.ext_delay_sec).trim(),
        }),
        TransferLine: data.transfer_line,
      }),
      ...(functionType === "warm_transfer" && {
        WarmSummaryPrompt: data.warm_summary_prompt,
        ...(data.failure_mode === "custom" && {
          FailureMode: "custom",
          FailurePrompt: data.failure_prompt,
        }),
      }),
    };
  };

  const saveSection = async (sectionId) => {
    const section = findFunctionSection(functionType, sectionId);
    if (!section || !editFunctionData?.Id) return;

    if (section.fields.length > 0) {
      const fieldsToValidate = section.fields.includes("name")
        ? section.fields
        : ["name", ...section.fields];
      const isValid = await trigger(fieldsToValidate);
      if (!isValid) return;
    }

    setSectionSaveState((prev) => ({ ...prev, [sectionId]: "saving" }));
    setSectionSaveError((prev) => ({ ...prev, [sectionId]: "" }));

    try {
      const data = getValues();
      const payload = buildSectionPayload(data, sectionId);

      const userData = await usersPostRequest(token, "/api/apiFunction", {
        practiceId: PracticeId,
        memberType,
        functionData: payload,
      });

      if (userData === "Token is expired, login again") {
        setIsTokenExpired(true);
        setSectionSaveState((prev) => ({ ...prev, [sectionId]: "idle" }));
        return;
      }

      if (userData !== "Function updated successfully") {
        const message =
          typeof userData === "string"
            ? userData
            : userData?.error || "Something went wrong. Please try again.";
        setSectionSaveState((prev) => ({ ...prev, [sectionId]: "error" }));
        setSectionSaveError((prev) => ({ ...prev, [sectionId]: message }));
        return;
      }

      fetchApiHandle("functionModal");
      setSectionSaveState((prev) => ({ ...prev, [sectionId]: "saved" }));
      setTimeout(() => {
        setSectionSaveState((prev) =>
          prev[sectionId] === "saved" ? { ...prev, [sectionId]: "idle" } : prev
        );
      }, 2500);
    } catch (error) {
      setSectionSaveState((prev) => ({ ...prev, [sectionId]: "error" }));
      setSectionSaveError((prev) => ({
        ...prev,
        [sectionId]: "Something went wrong. Please try again.",
      }));
    }
  };

  const handleRemoveAttribute = (index) => {
    const attributeToDelete = getValues(`attributes.${index}`);
    if (attributeToDelete?.attributeId) {
      setDeletedAttributeIds((prev) => [
        ...prev,
        attributeToDelete.attributeId,
      ]);
    }
    remove(index);
  };

  useEffect(() => {
    if (editFunctionData) {
      originalEditData.current = editFunctionData;
      setCurrentFunctionType(editFunctionData.FunctionType);

      const baseData = {
        func_type: editFunctionData.FunctionType,
        name: editFunctionData.Functions?.Name || "",
        description: editFunctionData.Functions?.Description || "",
      };

      if (editFunctionData.FunctionType === "apiFunction") {
        reset({
          ...baseData,
          ...buildApiFunctionValues(editFunctionData.Functions),
        });
      } else if (
        editFunctionData.FunctionType === "warm_transfer" ||
        editFunctionData.FunctionType === "cold_transfer"
      ) {
        reset({
          ...baseData,
          ...buildTransferFunctionValues(
            editFunctionData.FunctionType,
            editFunctionData.Functions
          ),
        });
      } else {
        reset(baseData);
      }
    } else {
      reset(buildDefaultFormValues(initialFunctionType));
      setAvailability(false);
      setDeletedAttributeIds([]);
      setDeletedChildAttributeIds([]);
      setFunctionName("");
      setCurrentFunctionType("");
    }
  }, [editFunctionData, initialFunctionType, reset]);

  useEffect(() => {
    if (!functionType) return;

    if (editFunctionData && currentFunctionType) {
      const originalType = originalEditData.current?.FunctionType;

      if (currentFunctionType === originalType) {
        // Restore saved values
        const restoreData = originalEditData.current;

        if (restoreData.FunctionType === "apiFunction") {
          reset(buildApiFunctionValues(restoreData.Functions));
        } else if (
          restoreData.FunctionType === "warm_transfer" ||
          restoreData.FunctionType === "cold_transfer"
        ) {
          reset(
            buildTransferFunctionValues(
              restoreData.FunctionType,
              restoreData.Functions
            )
          );
        }
      } else {
        // Switching type → reset blank defaults for that new type
        reset(buildDefaultFormValues(functionType));
        setCurrentFunctionType("");
      }
      return;
    }

    // === CREATE MODE (new function, not editing) ===
    reset(buildDefaultFormValues(functionType || initialFunctionType));
    // setCurrentFunctionType("")
  }, [functionType, editFunctionData, reset, currentFunctionType, initialFunctionType]);

  useEffect(() => {
    if (!show) {
      reset(buildDefaultFormValues(initialFunctionType));
      setAvailability(false);
      setDeletedAttributeIds([]);
      setDeletedChildAttributeIds([]);
      setSuccessMsg("");
      setFunctionName("");
      setCurrentFunctionType("");
      setSectionSaveState({});
      setSectionSaveError({});
    }
  }, [show, reset, initialFunctionType]);

  useEffect(() => {
    if (!show || editFunctionData) return;

    reset(buildDefaultFormValues(initialFunctionType));
    setCurrentFunctionType(initialFunctionType);
  }, [show, editFunctionData, initialFunctionType, reset]);

  // Fetch outbound trunk status to gate transfer functions
  useEffect(() => {
    const fetchOutboundStatus = async () => {
      try {
        const resp = await dateRangeRequest(token, "/api/checkOutboundTrunk", {
          practiceId: PracticeId,
        });
        if (resp === "Token is expired, login again") {
          setIsTokenExpired(true);
        } else if (resp && typeof resp.hasOutboundTrunk === "boolean") {
          setHasOutboundTrunk(resp.hasOutboundTrunk);
        } else {
          setHasOutboundTrunk(false);
        }
      } catch (e) {
        setHasOutboundTrunk(false);
      }
    };

    if (show && PracticeId && token) {
      fetchOutboundStatus();
    }
  }, [show, PracticeId, token, dateRangeRequest]);

  // Token-expiry redirect is now handled once, centrally, in ContextApi.js - see its comment
  // for why this component's own copy of the same effect was removed.

  const clearSuccessOnFocus = () => {
    if (successMsg) setSuccessMsg("");
  };

  const handleColdTransferImported = () => {
    fetchApiHandle("functionModal");
    handleClose();
  };

  const functionTypeField = (
    <div className="mb-3">
      <label className="form-label">
        <span className="text-danger me-1">*</span>Function Type
      </label>
      <select
        className={`form-select px-2 ${errors.func_type ? "is-invalid" : ""
          }`}
        disabled={isEditingSameType}
        {...register("func_type", {
          required: true,
          onChange: (e) => setCurrentFunctionType(e.target.value),
        })}
      >
        <option value="">Select function type</option>
        <option value="apiFunction">Api function</option>
        <option
          value="warm_transfer"
          disabled={!hasOutboundTrunk && currentFunctionType !== "warm_transfer"}
          style={{
            color:
              !hasOutboundTrunk && currentFunctionType !== "warm_transfer"
                ? "#6c757d"
                : undefined,
          }}
        >
          Warm transfer {!hasOutboundTrunk ? "(setup outbound number to activate)" : ""}
        </option>
        <option
          value="cold_transfer"
          disabled={!hasOutboundTrunk && currentFunctionType !== "cold_transfer"}
          style={{
            color:
              !hasOutboundTrunk && currentFunctionType !== "cold_transfer"
                ? "#6c757d"
                : undefined,
          }}
        >
          Cold transfer {!hasOutboundTrunk ? "(setup outbound number to activate)" : ""}
        </option>
      </select>
      {!hasOutboundTrunk && (
        <div className="form-text text-warning">
          Warm / Cold transfer require an outbound number. Configure it in Phone Settings to enable.
        </div>
      )}
      {isEditingSameType && (
        <div className="form-text">
          Function type can&apos;t be changed once a function has sections saved individually.
        </div>
      )}
    </div>
  );

  const renderGeneralFields = () => (
    <>
      {/* Name Field */}
      <div className="mb-3 position-relative">
        <label htmlFor="name" className="form-label">
          <span className="text-danger me-1">*</span>Name
        </label>
        <input
          type="text"
          className={`form-control px-2 ${errors.name ? "is-invalid" : ""
            }`}
          id="name"
          placeholder="Enter function name"
          onKeyDown={(e) => e.key === " " && e.preventDefault()}
          {...register("name", {
            required: true,
            maxLength: {
              value: 255,
              message: "Max 255 characters allowed",
            },
            validate: (value) => {
              setFunctionName(value);
              const trimmedValue = value.trim();
              if (/\s/.test(trimmedValue))
                return "Spaces are not allowed. Use underscores instead";
              if (
                !/^[a-z](?:[a-z0-9]*(_[a-z0-9]+)*)?$/.test(trimmedValue)
              ) {
                return "Must start with a letter, only lowercase letters, numbers, and underscores allowed (underscore only in the middle)";
              }
              if (trimmedValue.length === 0) {
                setAvailability(false);
                return "Name cannot be just spaces";
              }
              if (
                editFunctionData &&
                editFunctionData.Functions.Name.toLowerCase() ===
                trimmedValue.toLowerCase()
              ) {
                setAvailability(true);
                return true;
              }
              return new Promise((resolve) => {
                checkAvailability(trimmedValue, resolve);
              });
            },
          })}
        />
        <div className="position-absolute top-50 end-0 px-2 pt-1">
          {isValidating && !errors.name && functionName && (
            <div
              className="spinner-border spinner-border-sm"
              role="status"
            >
              <span className="visually-hidden">Loading...</span>
            </div>
          )}
          {!isValidating &&
            availability &&
            !errors.name &&
            functionName && (
              <FontAwesomeIcon
                className="text-success"
                icon={faCircleCheck}
              />
            )}
          {!isValidating &&
            !availability &&
            !errors.name &&
            functionName && (
              <FontAwesomeIcon
                className="text-danger"
                icon={faCircleXmark}
              />
            )}
        </div>
        {errors.name && (
          <div className="invalid-feedback d-block">
            {errors.name.message}
          </div>
        )}
      </div>

      {/* Description */}
      <div className="mb-3">
        <label className="form-label">
          <span className="text-danger me-1">*</span>Description
        </label>
        <textarea
          placeholder="Enter description"
          className={`form-control px-2 ${errors.description ? "is-invalid" : ""
            }`}
          rows="3"
          {...register("description", { required: true })}
        />
      </div>
    </>
  );

  const renderRequestFields = () => (
    <>
      {/* URL */}
      <div className="mb-3">
        <label htmlFor="url" className="form-label">
          <span className="text-danger me-1">*</span>URL
        </label>
        <input
          type="url"
          className={`form-control px-2 ${errors.url ? "is-invalid" : ""
            }`}
          id="url"
          placeholder="https://example.com/api"
          {...register("url", { required: true })}
        />
      </div>

      {/* Request Type */}
      <div className="mb-3">
        <label className="form-label">
          <span className="text-danger me-1">*</span>Method
        </label>
        <select
          className={`form-select px-2 ${errors.requestType ? "is-invalid" : ""
            }`}
          {...register("requestType", { required: true })}
        >
          <option value="">Select method</option>
          <option value="GET">GET</option>
          <option value="POST">POST</option>
          <option value="PUT">PUT</option>
          <option value="DELETE">DELETE</option>
          <option value="PATCH">PATCH</option>
        </select>
      </div>

      {/* Authorization header */}
      <div className="mb-3">
        <label htmlFor="auth_header" className="form-label">
          <span className="text-danger me-1">*</span>Authorization
          header
        </label>
        <input
          className={`form-control px-2 ${errors.auth_header ? "is-invalid" : ""
            }`}
          id="auth_header"
          placeholder="Enter authorization header"
          {...register("auth_header", { required: true })}
        />
      </div>
    </>
  );

  const renderExecutionSettingsFields = () => (
    <div className="border rounded p-3 mb-3">
      <h6 className="mb-3">Execution Settings</h6>

      <div className="mb-3">
        <label htmlFor="execution_mode" className="form-label">
          <span className="text-danger me-1">*</span>Execution Mode
        </label>
        <select
          id="execution_mode"
          className={`form-select px-2 ${errors.execution_mode ? "is-invalid" : ""
            }`}
          {...register("execution_mode", { required: true })}
        >
          <option value="standard">Standard</option>
          <option value="stateful">Stateful</option>
        </select>
        <div className="form-text">
          Standard sends a single request from one tool call. Stateful exposes
          capture/submit tools so the worker can accumulate partial fields during
          the call and submit once at the end.
        </div>
      </div>

      <div className="mb-3">
        <label htmlFor="timeout_sec" className="form-label">
          <span className="text-danger me-1">*</span>Timeout
          (seconds)
        </label>
        <input
          type="number"
          min="1"
          max="120"
          className={`form-control px-2 ${errors.timeout_sec ? "is-invalid" : ""
            }`}
          id="timeout_sec"
          placeholder="Enter timeout in seconds"
          {...register("timeout_sec", {
            required: "Timeout is required",
            valueAsNumber: true,
            min: {
              value: 1,
              message: "Timeout must be at least 1 second",
            },
            max: {
              value: 120,
              message: "Timeout cannot exceed 120 seconds",
            },
            validate: (value) =>
              Number.isInteger(value) ||
              "Timeout must be a whole number",
          })}
        />
        {errors.timeout_sec && (
          <div className="invalid-feedback d-block">
            {errors.timeout_sec.message}
          </div>
        )}
      </div>

      <div className="mb-3">
        <label htmlFor="retry_count" className="form-label">
          <span className="text-danger me-1">*</span>Retry Count
        </label>
        <input
          type="number"
          min="0"
          max="3"
          className={`form-control px-2 ${errors.retry_count ? "is-invalid" : ""
            }`}
          id="retry_count"
          placeholder="Enter retry count"
          {...register("retry_count", {
            required: "Retry count is required",
            valueAsNumber: true,
            min: {
              value: 0,
              message: "Retry count cannot be negative",
            },
            max: {
              value: 3,
              message: "Retry count cannot exceed 3",
            },
            validate: (value) =>
              Number.isInteger(value) ||
              "Retry count must be a whole number",
          })}
        />
        <div className="form-text">
          Retry count is only honored for GET requests in this
          version.
        </div>
        {errors.retry_count && (
          <div className="invalid-feedback d-block">
            {errors.retry_count.message}
          </div>
        )}
      </div>

      <div className="mb-0">
        <label htmlFor="periodic_message" className="form-label">
          Periodic Message
        </label>
        <textarea
          id="periodic_message"
          rows="3"
          placeholder="Enter periodic message"
          className={`form-control px-2 ${errors.periodic_message ? "is-invalid" : ""
            }`}
          {...register("periodic_message", {
            validate: (value) => {
              const trimmedValue =
                typeof value === "string" ? value.trim() : "";
              if (trimmedValue.length > 255) {
                return "Max 255 characters allowed";
              }
              return true;
            },
          })}
        />
        <div className="form-text">
          Used when the API call is still running during the call.
        </div>
        {errors.periodic_message && (
          <div className="invalid-feedback d-block">
            {errors.periodic_message.message}
          </div>
        )}
      </div>
    </div>
  );

  const renderRoutingRulesFields = () => (
    <div className="border rounded p-3 mb-3">
      <h6 className="mb-2">Routing Rules (optional)</h6>
      <div className="form-text mb-3">
        Send this to a different URL when one of this tool&apos;s own
        date attributes falls a certain number of days from today —
        e.g. appointment confirmations 2-3 days out go to a
        different endpoint than the rest. Evaluated in code before
        the request fires, not by the model — leave both Min and
        Max blank to match a specific date exactly. Rules are
        checked in order; the first match wins, and if nothing
        matches this tool&apos;s own URL above is used.
      </div>

      {routingRuleFields.map((field, index) => (
        <div key={field.id} className="border rounded p-2 mb-2">
          <div className="row g-2">
            <div className="col-md-4">
              <label className="form-label">Date field</label>
              <select
                className="form-select px-2"
                {...register(`routing_rules.${index}.field`)}
              >
                <option value="">Select an attribute</option>
                {routableAttributeNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Min days</label>
              <input
                type="number"
                className="form-control px-2"
                placeholder="e.g. 2"
                {...register(`routing_rules.${index}.min`)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Max days</label>
              <input
                type="number"
                className="form-control px-2"
                placeholder="e.g. 3"
                {...register(`routing_rules.${index}.max`)}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Target URL</label>
              <input
                className="form-control px-2"
                placeholder="https://..."
                {...register(`routing_rules.${index}.target_url`)}
              />
            </div>
            <div className="col-md-8">
              <label className="form-label">
                Target authorization header (optional)
              </label>
              <input
                className="form-control px-2"
                placeholder="Leave blank to reuse this tool's own authorization header"
                {...register(
                  `routing_rules.${index}.target_auth_header`
                )}
              />
            </div>
            <div className="col-md-4 d-flex align-items-end">
              <span
                className="simple-btn d-block text-danger"
                onClick={() => removeRoutingRule(index)}
              >
                Remove rule
              </span>
            </div>
          </div>
        </div>
      ))}

      <span
        className="simple-btn d-block"
        onClick={() =>
          appendRoutingRule({
            field: "",
            min: "",
            max: "",
            target_url: "",
            target_auth_header: "",
          })
        }
      >
        + Add Routing Rule
      </span>
    </div>
  );

  const renderAttributesFields = () => (
    <>
      {/* Dynamic Attributes */}
      {fields.map((field, index) => (
        <AttributeField
          key={field.id}
          field={field}
          index={index}
          executionMode={executionMode}
          control={control}
          register={register}
          errors={errors}
          getValues={getValues}
          removeAttribute={handleRemoveAttribute}
          setDeletedChildAttributeIds={setDeletedChildAttributeIds}
        />
      ))}

      {/* Add Attribute */}
      {!(
        requestTypeValue === "GET" || requestTypeValue === "DELETE"
      ) && (
          <div className="mt-3">
            <span
              className="simple-btn d-block"
              onClick={() =>
                append({
                  name: "",
                  description: "",
                  attr_type: "",
                  required: "false",
                  source: "model",
                  default_value: "",
                  aliases: "",
                  childAttributes: [],
                })
              }
            >
              + Add Attribute
            </span>
          </div>
        )}
    </>
  );

  const renderTransferDetailsFields = () => (
    <>
      <div className="mb-3">
        <label
          htmlFor="handover_receiver_phone_number"
          className="form-label"
        >
          <span className="text-danger me-1">*</span>Handover receiver
          phone number
        </label>
        <input
          type="text"
          className={`form-control px-2 ${errors.handover_receiver_phone_number ? "is-invalid" : ""
            }`}
          id="handover_receiver_phone_number"
          placeholder="Enter phone number"
          {...register("handover_receiver_phone_number", {
            required: true,
            pattern: {
              value: /^(?:\d{3}-\d{3}-\d{4}|\d{11}|\d{12})$/,
              message: "Invalid phone number format",
            },
          })}
        />
      </div>

      <div className="mb-3">
        <label htmlFor="ext" className="form-label">
          Extension
        </label>
        <input
          type="text"
          className={`form-control px-2 ${errors.ext ? "is-invalid" : ""
            }`}
          id="ext"
          placeholder="Enter extension number"
          {...register("ext", {
            pattern: {
              value: /^(w*[0-9]+)$/,
              message: "Invalid extension format",
            },
          })}
        />
      </div>

      <div className="mb-3">
        <label htmlFor="ext_delay_sec" className="form-label">
          Wait before extension (seconds, optional)
        </label>
        <input
          type="number"
          min="0"
          max="30"
          step="0.5"
          className={`form-control px-2 ${errors.ext_delay_sec ? "is-invalid" : ""}`}
          id="ext_delay_sec"
          placeholder="e.g. 2 (waits before typing the extension)"
          {...register("ext_delay_sec", {
            min: { value: 0, message: "Must be 0 or more" },
            max: { value: 30, message: "Must be 30 or less" },
          })}
        />
      </div>

      <div className="mb-3">
        <label htmlFor="transfer_line" className="form-label">
          <span className="text-danger me-1">*</span>Transfer Line (What to say before transferring)
        </label>
        <textarea
          placeholder="Enter transfer line"
          className={`form-control px-2 ${errors.transfer_line ? "is-invalid" : ""
            }`}
          rows="2"
          {...register("transfer_line", { required: true })}
        />
      </div>
    </>
  );

  const renderWarmSummaryField = () => (
    <div className="mb-3">
      <label className="form-label">
        <span className="text-danger me-1">*</span>Warm summary
        prompt
      </label>
      <textarea
        placeholder="Enter prompt"
        className={`form-control px-2 ${errors.warm_summary_prompt ? "is-invalid" : ""
          }`}
        rows="3"
        {...register("warm_summary_prompt", { required: true })}
      />
    </div>
  );

  const failureModeValue = watch("failure_mode");
  const renderNoAnswerField = () => (
    <div className="mb-3">
      <label htmlFor="failure_mode" className="form-label">
        If nobody picks up
      </label>
      <select
        id="failure_mode"
        className="form-select px-2"
        {...register("failure_mode")}
      >
        <option value="default">Default (busy / could not reach message)</option>
        <option value="custom">Custom instructions</option>
      </select>
      {failureModeValue === "custom" && (
        <textarea
          placeholder="Example: Tell the caller the line was busy and someone will call them back, but you need a few details to notify them. Ask for their name, date of birth and callback number."
          className={`form-control px-2 mt-2 ${errors.failure_prompt ? "is-invalid" : ""}`}
          rows="4"
          maxLength={4000}
          {...register("failure_prompt", {
            validate: (value) =>
              watch("failure_mode") !== "custom" ||
              Boolean(String(value || "").trim()) ||
              "Write the instructions or switch back to Default",
          })}
        />
      )}
    </div>
  );

  const renderBulkUploadSection = () =>
    functionType === "cold_transfer" && !editFunctionData ? (
      <ColdTransferCsvUpload
        memberType={memberType}
        onImported={handleColdTransferImported}
        onTokenExpired={() => setIsTokenExpired(true)}
        resetKey={`${show}-${functionType}-${Boolean(editFunctionData)}`}
      />
    ) : (
      <div className="form-text">
        Bulk upload is only available while creating brand new cold transfer
        functions, not while editing an existing one.
      </div>
    );

  const renderSectionBody = (sectionId) => {
    switch (sectionId) {
      case "general":
        return renderGeneralFields();
      case "request":
        return renderRequestFields();
      case "executionSettings":
        return renderExecutionSettingsFields();
      case "routingRules":
        return renderRoutingRulesFields();
      case "attributes":
        return renderAttributesFields();
      case "transferDetails":
        return renderTransferDetailsFields();
      case "warmSummary":
        return renderWarmSummaryField();
      case "noAnswer":
        return renderNoAnswerField();
      case "bulkUpload":
        return renderBulkUploadSection();
      default:
        return null;
    }
  };

  const renderSectionedLayout = () => (
    <div className="function-modal-body">
      <div
        className="api-integration-mini-sidebar function-modal-nav"
        role="tablist"
        aria-label="Function section"
      >
        {sections.map((section) => {
          const state = sectionSaveState[section.id];
          return (
            <button
              key={section.id}
              type="button"
              role="tab"
              aria-selected={activeSection === section.id}
              className={`api-integration-mini-sidebar__item ${activeSection === section.id
                ? "api-integration-mini-sidebar__item--active"
                : ""
                }`}
              onClick={() => setActiveSection(section.id)}
            >
              {section.label}
              {state === "saved" && (
                <FontAwesomeIcon
                  icon={faCircleCheck}
                  className="text-success ms-2"
                />
              )}
            </button>
          );
        })}
      </div>

      <div className="function-modal-panel">
        {functionTypeField}
        {activeSection && (
          // key={activeSection} forces a full unmount/remount of this section's DOM subtree on
          // every switch - without it, React (seeing the same element type/position across two
          // different sections, e.g. both render a "mb-3 > input" as their first child) can reuse
          // the SAME underlying <input> DOM node for two differently-registered fields. Since
          // react-hook-form's register() uses uncontrolled inputs (reads straight from the DOM
          // ref, no controlled `value` prop), a reused node keeps its old typed-in DOM value even
          // after React relabels it with a new name/id - e.g. switching General -> Transfer
          // Details -> General back could leave the Name field showing the phone number that used
          // to occupy that same DOM slot. Remounting guarantees each field gets its own fresh node.
          <div className="function-modal-panel__section" key={activeSection}>
            <h6 className="mb-3">
              {findFunctionSection(functionType, activeSection)?.label}
            </h6>
            {renderSectionBody(activeSection)}

            {activeSection !== "bulkUpload" && (
              <div className="d-flex align-items-center mt-3">
                <button
                  type="button"
                  className="simple-btn"
                  disabled={sectionSaveState[activeSection] === "saving"}
                  onClick={() => saveSection(activeSection)}
                >
                  {sectionSaveState[activeSection] === "saving" ? (
                    <div
                      className="spinner-border spinner-border-sm"
                      role="status"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  ) : (
                    "Save"
                  )}
                </button>
                {sectionSaveState[activeSection] === "saved" && (
                  <div className="text-success ms-3">
                    <FontAwesomeIcon icon={faCircleCheck} /> Saved
                  </div>
                )}
                {sectionSaveState[activeSection] === "error" && (
                  <div className="text-danger ms-3">
                    {sectionSaveError[activeSection] ||
                      "Something went wrong. Please try again."}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const renderSingleFormLayout = () => (
    <>
      {functionTypeField}

      {functionType && (
        <div className="mb-3">{renderGeneralFields()}</div>
      )}

      {functionType === "apiFunction" ? (
        <>
          {renderRequestFields()}
          {renderExecutionSettingsFields()}
          {renderRoutingRulesFields()}
          {renderAttributesFields()}
        </>
      ) : functionType === "warm_transfer" ||
        functionType === "cold_transfer" ? (
        <>
          {renderTransferDetailsFields()}
          {functionType === "cold_transfer" && !editFunctionData && (
            <ColdTransferCsvUpload
              memberType={memberType}
              onImported={handleColdTransferImported}
              onTokenExpired={() => setIsTokenExpired(true)}
              resetKey={`${show}-${functionType}-${Boolean(editFunctionData)}`}
            />
          )}
          {functionType === "warm_transfer" && renderWarmSummaryField()}
          {functionType === "warm_transfer" && renderNoAnswerField()}
        </>
      ) : (
        ""
      )}
    </>
  );

  return (
    <Modal show={show} onHide={handleClose} className="popup">
      <Modal.Header closeButton>
        <Modal.Title>
          {isEditingSameType ? "Edit API Function" : "Add API Function"}
        </Modal.Title>
      </Modal.Header>

      {isEditingSameType ? (
        <>
          <Modal.Body>{renderSectionedLayout()}</Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="simple-btn ms-3"
              onClick={handleClose}
            >
              Close
            </button>
          </Modal.Footer>
        </>
      ) : (
        <form
          className="custom-form"
          onFocusCapture={clearSuccessOnFocus}
          onSubmit={handleSubmit(onSubmit)}
        >
          <Modal.Body>{renderSingleFormLayout()}</Modal.Body>

          <Modal.Footer>
            <div className="d-flex align-items-center">
              {successMsg && (
                <div className="text-success fs-6">
                  <FontAwesomeIcon icon={faCircleCheck} /> {successMsg}
                </div>
              )}
              <button
                disabled={isSubmitting}
                type="submit"
                className={`simple-btn ms-3 ${isSubmitting ? "px-4" : ""}`}
              >
                {isSubmitting ? (
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                ) : (
                  "Submit"
                )}
              </button>
            </div>
          </Modal.Footer>
        </form>
      )}
    </Modal>
  );
};
